// Fixed assets: big purchases (vehicles, equipment, buildings) written down
// a little each period as they wear out. The list shows each item and what
// it's still worth; the detail shows the write-down so far.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type AssetCategory,
  type FixedAsset,
  createAssetCategory,
  createFixedAsset,
  listAssetCategories,
  listFixedAssets,
  postDepreciationJournals,
  runDepreciation,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";

const METHODS = [
  "straight_line",
  "declining_balance",
  "sum_of_years_digits",
  "units_of_production",
] as const;
const CONVENTIONS = [
  "full_month",
  "mid_month",
  "half_year",
  "mid_quarter",
  "pro_rata_days",
  "full_at_purchase",
] as const;

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "in_service":
    case "active":
      return "info";
    case "fully_depreciated":
      return "success";
    case "disposed":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function toCents(amount: string, allowZero: boolean): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return allowZero ? 0 : null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || (!allowZero && value === 0))
    return null;
  const cents = Math.round(value * 100);
  return Number.isSafeInteger(cents) ? cents : null;
}

function describeRunResult(result: {
  processed: number;
  skipped: number;
}): string {
  if (result.processed === 0)
    return "Nothing due — every asset is up to date.";
  return `Depreciated ${result.processed} asset${
    result.processed === 1 ? "" : "s"
  }${result.skipped > 0 ? ` (${result.skipped} skipped)` : ""}.`;
}

export function FixedAssetsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<FixedAsset[] | null>(null);
  const [categories, setCategories] = useState<AssetCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState<"category" | "asset" | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const [postingLedger, setPostingLedger] = useState(false);
  const [confirmPostLedger, setConfirmPostLedger] = useState(false);
  const [ledgerNotice, setLedgerNotice] = useState<{
    message: string;
    journalEntryIds: string[];
  } | null>(null);

  // Client-side controls over the fetched list (the list endpoint has no
  // search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listFixedAssets()
      .then(setAssets)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
    listAssetCategories()
      .then(setCategories)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, []);

  useEffect(reload, [reload]);

  const runAllDepreciation = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await runDepreciation();
      setNotice(describeRunResult(result));
      setConfirmRun(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmRun(false);
    } finally {
      setRunning(false);
    }
  };

  const postDepreciationToLedger = async () => {
    setPostingLedger(true);
    setError(null);
    setNotice(null);
    setLedgerNotice(null);
    try {
      const result = await postDepreciationJournals();
      const count = result?.posted_count ?? 0;
      const ids = Array.isArray(result?.journal_entry_ids)
        ? result.journal_entry_ids
        : [];
      setLedgerNotice({
        message:
          count === 0
            ? "Nothing to post — every depreciation entry is already in the ledger."
            : `Posted ${count} depreciation journal${count === 1 ? "" : "s"} to the ledger.`,
        journalEntryIds: ids,
      });
      setConfirmPostLedger(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmPostLedger(false);
    } finally {
      setPostingLedger(false);
    }
  };

  const statusCounts = (assets ?? []).reduce<Record<string, number>>(
    (counts, asset) => {
      const key = asset.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  const visibleAssets = (assets ?? [])
    .filter((asset) => !statusTab || asset.status.toLowerCase() === statusTab)
    .filter((asset) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        asset.name.toLowerCase().includes(query) ||
        asset.asset_number.toLowerCase().includes(query)
      );
    });

  const actions = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setConfirmRun(true)}
        disabled={running}
        title="Post this period's depreciation for every eligible asset across the organization"
      >
        {running ? "Running…" : "Run depreciation"}
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          setError(null);
          setLedgerNotice(null);
          setConfirmPostLedger(true);
        }}
        disabled={postingLedger}
        title="Post every calculated but unposted depreciation entry to this legal entity's ledger"
      >
        {postingLedger ? "Posting…" : "Post depreciation to ledger"}
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setCreating("category")}
      >
        New category
      </Button>
      <Button type="button" onClick={() => setCreating("asset")}>
        New asset
      </Button>
    </>
  );

  return (
    <div className="grid gap-6">
      {embedded ? (
        <div className="actions-row">{actions}</div>
      ) : (
        <PageHeader
          title="Fixed assets"
          description="Big purchases written down over time — what each item cost and what it's still worth."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {ledgerNotice ? (
        <output
          data-testid="depreciation-ledger-notice"
          className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]"
        >
          {ledgerNotice.message}
          {ledgerNotice.journalEntryIds.length > 0 ? (
            <ul
              aria-label="Posted depreciation journals"
              className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs"
            >
              {ledgerNotice.journalEntryIds.map((entryId) => (
                <li key={entryId}>
                  <Link
                    to="/journal/$entryId"
                    params={{ entryId }}
                    className="underline underline-offset-4"
                  >
                    {entryId}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </output>
      ) : null}

      {error && assets === null ? null : (
        <Tabs
          label="Filter fixed assets by status"
          tabs={[
            { value: "", label: `All (${(assets ?? []).length})` },
            ...statusTabs.map((status) => ({
              value: status,
              label: `${plainStatus(status)} (${statusCounts[status]})`,
            })),
          ]}
          value={statusTab}
          onValueChange={setStatusTab}
        >
          <div className="surface grid gap-3 rounded-2xl p-4">
            <div className="flex flex-wrap items-end gap-3">
              <label
                htmlFor="fixed-assets-search"
                className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
              >
                Search
                <Input
                  id="fixed-assets-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name or asset number…"
                  disabled={loading}
                />
              </label>
              <Button
                type="button"
                variant="secondary"
                onClick={reload}
                disabled={loading}
              >
                {loading ? "Loading…" : "Refresh"}
              </Button>
            </div>
          </div>

          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="fixed-assets-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Written down</TableHead>
                    <TableHead className="text-right">Still worth</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && assets === null ? (
                    <TableRow>
                      <TableCell colSpan={5}>Loading…</TableCell>
                    </TableRow>
                  ) : (assets ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        No fixed assets yet — add the first one above.
                      </TableCell>
                    </TableRow>
                  ) : visibleAssets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        No assets match these filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell>
                          <Link
                            to="/fixed-assets/$assetId"
                            params={{ assetId: asset.id }}
                            className="font-medium hover:underline"
                          >
                            {asset.name}
                          </Link>
                          <span className="block text-xs text-[var(--muted-foreground)]">
                            {asset.asset_number}
                            {asset.is_cwip ? " · Still being built" : ""}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: asset.purchase_price_cents,
                            currency: "USD",
                          })}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title="Accumulated depreciation (technical term)"
                        >
                          {formatMoney({
                            cents: asset.accumulated_depreciation_cents,
                            currency: "USD",
                          })}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title="Net book value (technical term)"
                        >
                          {formatMoney({
                            cents: asset.net_book_value_cents,
                            currency: "USD",
                          })}
                        </TableCell>
                        <TableCell>
                          <StatusDot
                            tone={statusTone(asset.status)}
                            label={humanizeLabel(plainStatus(asset.status))}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="fixed-assets-stack"
          >
            {loading && assets === null ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                Loading…
              </p>
            ) : (assets ?? []).length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                No fixed assets yet — add the first one above.
              </p>
            ) : visibleAssets.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                No assets match these filters.
              </p>
            ) : (
              visibleAssets.map((asset) => (
                <article
                  key={asset.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <Link
                      to="/fixed-assets/$assetId"
                      params={{ assetId: asset.id }}
                      className="record-title underline-offset-4 hover:underline"
                    >
                      {asset.name}
                    </Link>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: asset.purchase_price_cents,
                        currency: "USD",
                      })}
                    </span>
                  </div>
                  <CardField label="Asset number">
                    {asset.asset_number}
                    {asset.is_cwip ? " · Still being built" : ""}
                  </CardField>
                  <CardField label="Written down">
                    {formatMoney({
                      cents: asset.accumulated_depreciation_cents,
                      currency: "USD",
                    })}
                  </CardField>
                  <CardField label="Still worth">
                    {formatMoney({
                      cents: asset.net_book_value_cents,
                      currency: "USD",
                    })}
                  </CardField>
                  <CardField label="Status">
                    <StatusDot
                      tone={statusTone(asset.status)}
                      label={humanizeLabel(plainStatus(asset.status))}
                    />
                  </CardField>
                  <div className="flex flex-wrap gap-2 pt-3">
                    <Button asChild variant="secondary" size="sm">
                      <Link
                        to="/fixed-assets/$assetId"
                        params={{ assetId: asset.id }}
                      >
                        Open asset
                      </Link>
                    </Button>
                  </div>
                </article>
              ))
            )}
          </div>
        </Tabs>
      )}

      {categories && categories.length > 0 ? (
        <section aria-label="Asset categories" className="grid gap-3">
          <h2 className="text-lg font-semibold tracking-tight">Categories</h2>
          <div className="surface overflow-hidden rounded-2xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Life</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      {humanizeLabel(category.default_depreciation_method)}
                    </TableCell>
                    <TableCell>
                      {category.default_useful_life_months
                        ? `${category.default_useful_life_months} months`
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ) : null}

      <CreateCategoryDrawer
        open={creating === "category"}
        onClose={() => setCreating(null)}
        onDone={() => {
          setCreating(null);
          reload();
        }}
      />
      <CreateAssetDrawer
        open={creating === "asset"}
        categories={categories ?? []}
        onClose={() => setCreating(null)}
        onCreated={(assetId) => {
          reload();
          void navigate({ to: "/fixed-assets/$assetId", params: { assetId } });
        }}
      />

      <Dialog
        open={confirmPostLedger}
        onOpenChange={(open) => {
          if (!open && !postingLedger) setConfirmPostLedger(false);
        }}
        title="Post depreciation to the ledger?"
        description="Writes every depreciation entry already calculated for this legal entity into the ledger. It does not calculate new depreciation — run depreciation first. Nothing posts until you confirm."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmPostLedger(false)}
            disabled={postingLedger}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void postDepreciationToLedger()}
            disabled={postingLedger}
          >
            {postingLedger ? "Posting…" : "Post to ledger"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={confirmRun}
        onOpenChange={(open) => {
          if (!open && !running) setConfirmRun(false);
        }}
        title="Run depreciation for every eligible asset?"
        description="Posts this period's depreciation for every eligible asset in the organization — not just one asset. Assets already written down for the period are skipped."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRun(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void runAllDepreciation()}
            disabled={running}
          >
            {running ? "Running…" : "Post depreciation"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

// Creation drawers stay mounted beside the page so a closed panel keeps the
// draft in this browser session (same behavior as the invoice/bill pilots).
function CreateCategoryDrawer({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [method, setMethod] = useState<(typeof METHODS)[number]>("straight_line");
  const [convention, setConvention] =
    useState<(typeof CONVENTIONS)[number]>("full_month");
  const [life, setLife] = useState("60");
  const [residual, setResidual] = useState("0");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setMethod("straight_line");
    setConvention("full_month");
    setLife("60");
    setResidual("0");
    setError(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!name.trim()) throw new Error("Name is required.");
      const months = Number(life);
      if (!Number.isInteger(months) || months <= 0)
        throw new Error("Useful life must be a whole number of months.");
      const default_residual_value_cents = toCents(residual, true);
      if (default_residual_value_cents === null)
        throw new Error("Residual value must be zero or a positive amount.");
      await createAssetCategory({
        name: name.trim(),
        default_depreciation_method: method,
        default_convention: convention,
        default_useful_life_months: months,
        default_residual_value_cents,
        is_active: true,
      });
      reset();
      onDone();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="New asset category"
      description="Categories hold the default write-down method. Create one before an asset that should inherit those defaults. Closing this panel keeps your draft in this browser session."
      className="w-[min(34rem,100%)]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Back to fixed assets
          </Button>
          <Button
            type="submit"
            form="asset-category-create-form"
            disabled={saving}
            className="ms-auto"
          >
            {saving ? "Creating…" : "Create category"}
          </Button>
        </>
      }
    >
      <form
        id="asset-category-create-form"
        onSubmit={submit}
        className="grid gap-3"
        aria-label="Create asset category"
      >
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label
          htmlFor="asset-category-name"
          className="grid gap-1 text-xs font-medium"
        >
          Name
          <Input
            id="asset-category-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label
          htmlFor="asset-category-method"
          className="grid gap-1 text-xs font-medium"
        >
          Method
          <select
            id="asset-category-method"
            className="select"
            value={method}
            onChange={(event) =>
              setMethod(event.target.value as (typeof METHODS)[number])
            }
            disabled={saving}
          >
            {METHODS.map((item) => (
              <option key={item} value={item}>
                {humanizeLabel(item)}
              </option>
            ))}
          </select>
        </label>
        <label
          htmlFor="asset-category-convention"
          className="grid gap-1 text-xs font-medium"
        >
          First-period convention
          <select
            id="asset-category-convention"
            className="select"
            value={convention}
            onChange={(event) =>
              setConvention(event.target.value as (typeof CONVENTIONS)[number])
            }
            disabled={saving}
          >
            {CONVENTIONS.map((item) => (
              <option key={item} value={item}>
                {humanizeLabel(item)}
              </option>
            ))}
          </select>
        </label>
        <label
          htmlFor="asset-category-life"
          className="grid gap-1 text-xs font-medium"
        >
          Useful life (months)
          <Input
            id="asset-category-life"
            type="number"
            min="1"
            step="1"
            value={life}
            onChange={(event) => setLife(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label
          htmlFor="asset-category-residual"
          className="grid gap-1 text-xs font-medium"
        >
          Residual value (USD)
          <Input
            id="asset-category-residual"
            type="number"
            min="0"
            step="0.01"
            value={residual}
            onChange={(event) => setResidual(event.target.value)}
            required
            disabled={saving}
          />
        </label>
      </form>
    </Drawer>
  );
}

function CreateAssetDrawer({
  open,
  categories,
  onClose,
  onCreated,
}: {
  open: boolean;
  categories: AssetCategory[];
  onClose: () => void;
  onCreated: (assetId: string) => void;
}) {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(todayInput);
  const [price, setPrice] = useState("");
  const [life, setLife] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!name.trim() || !number.trim())
        throw new Error("Name and asset number are required.");
      if (!purchaseDate) throw new Error("Purchase date is required.");
      const purchase_price_cents = toCents(price, false);
      if (purchase_price_cents === null)
        throw new Error("Cost must be a positive amount.");
      const category = categories.find((item) => item.id === categoryId);
      const months = life.trim()
        ? Number(life)
        : category?.default_useful_life_months;
      if (months === undefined || !Number.isInteger(months) || months <= 0)
        throw new Error(
          "Useful life is required when the category does not set one.",
        );
      const created = await createFixedAsset({
        name: name.trim(),
        asset_number: number.trim(),
        category_id: categoryId || undefined,
        purchase_date: purchaseDate,
        purchase_price_cents,
        useful_life_months: months,
      });
      onCreated(created.id);
      onClose();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="New fixed asset"
      description="Records the purchase. Depreciation runs from the category defaults, or from the useful life entered here. Closing this panel keeps your draft in this browser session."
      className="w-[min(46rem,100%)]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Back to fixed assets
          </Button>
          <Button
            type="submit"
            form="asset-create-form"
            disabled={saving}
            className="ms-auto"
          >
            {saving ? "Creating…" : "Create asset"}
          </Button>
        </>
      }
    >
      <form
        id="asset-create-form"
        onSubmit={submit}
        className="grid gap-3"
        aria-label="Create fixed asset"
      >
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <label htmlFor="asset-name" className="grid gap-1 text-xs font-medium">
            Name
            <Input
              id="asset-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label
            htmlFor="asset-number"
            className="grid gap-1 text-xs font-medium"
          >
            Asset number
            <Input
              id="asset-number"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label
            htmlFor="asset-category"
            className="grid gap-1 text-xs font-medium"
          >
            Category
            <select
              id="asset-category"
              className="select"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              disabled={saving}
            >
              <option value="">No category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label
            htmlFor="asset-purchased"
            className="grid gap-1 text-xs font-medium"
          >
            Purchased
            <Input
              id="asset-purchased"
              type="date"
              value={purchaseDate}
              onChange={(event) => setPurchaseDate(event.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="asset-cost" className="grid gap-1 text-xs font-medium">
            Cost (USD)
            <Input
              id="asset-cost"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="asset-life" className="grid gap-1 text-xs font-medium">
            Useful life (months)
            <Input
              id="asset-life"
              type="number"
              min="1"
              step="1"
              value={life}
              onChange={(event) => setLife(event.target.value)}
              placeholder="Uses the category life when empty"
              disabled={saving}
            />
          </label>
        </div>
      </form>
    </Drawer>
  );
}
